/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { RightSheet } from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/index";
import type { Campaign, CampaignChannel, PromotionReward } from "@lemmonhq/types";
import { TargetingEditor } from "./TargetingEditor";
import type { CampaignFormData } from "./types";

const CHANNEL_OPTIONS: { value: CampaignChannel; label: string; icon: string }[] = [
  { value: "email", label: "Email", icon: "mail" },
  { value: "whatsapp", label: "WhatsApp", icon: "chat" },
  { value: "sms", label: "SMS", icon: "textsms" },
];

const TEMPLATE_VARS = ["{{customer_name}}", "{{coupon_code}}", "{{shop_name}}"];

export const CampaignEditor = defineComponent<{
  campaign?: Campaign;
  onClose: () => void;
  onSaved: (campaign: Campaign) => void;
}>({
  name: "CampaignEditor",
  props: ["campaign", "onClose", "onSaved"],
  setup(props) {
    const trpc = useTrpc();
    const queryClient = useQueryClient();

    const formData = ref<CampaignFormData>({
      name: props.campaign?.name ?? "",
      channels: (props.campaign?.channels as CampaignChannel[]) ?? ["email"],
      targeting: props.campaign?.targeting ?? {
        type: "conditions",
        groups: [
          {
            conditions: [
              { type: "last_order_days_ago", operator: "gte", value: 30 },
            ],
          },
        ],
      },
      message_template: props.campaign?.message_template ?? {
        subject: "",
        body: "Hi {{customer_name}}, here is your exclusive coupon: {{coupon_code}}",
      },
      coupon_expires_in_days: props.campaign?.coupon_expires_in_days ?? 30,
      promotion_reward: {
        type: "order_discount",
        mode: "percentage",
        value: 10,
      },
    });

    const createMutation = useMutation({
      mutationFn: (data: CampaignFormData) =>
        trpc.campaigns.create.mutate({
          name: data.name,
          targeting: data.targeting,
          channels: data.channels,
          message_template: data.message_template,
          coupon_expires_in_days: data.coupon_expires_in_days,
          promotion_reward: data.promotion_reward as any,
          promotion_requirements: [],
          promotion_days: {},
        }),
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: ["campaigns"] });
        props.onSaved(result as Campaign);
        props.onClose();
      },
    });

    const updateMutation = useMutation({
      mutationFn: (data: CampaignFormData) =>
        trpc.campaigns.update.mutate({
          id: props.campaign!.id,
          name: data.name,
          targeting: data.targeting,
          channels: data.channels,
          message_template: data.message_template,
          coupon_expires_in_days: data.coupon_expires_in_days,
        }),
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: ["campaigns"] });
        props.onSaved(result as Campaign);
        props.onClose();
      },
    });

    const saving = computed(
      () => createMutation.isPending.value || updateMutation.isPending.value,
    );

    const error = computed(
      () => createMutation.error.value || updateMutation.error.value,
    );

    const isValid = computed(() => formData.value.name.trim().length > 0);

    const handleSave = () => {
      if (!isValid.value) return;
      if (props.campaign) {
        updateMutation.mutate(formData.value);
      } else {
        createMutation.mutate(formData.value);
      }
    };

    const toggleChannel = (channel: CampaignChannel) => {
      const channels = formData.value.channels;
      if (channels.includes(channel)) {
        if (channels.length > 1) {
          formData.value.channels = channels.filter((c) => c !== channel);
        }
      } else {
        formData.value.channels = [...channels, channel];
      }
    };

    const updateReward = (patch: Partial<PromotionReward>) => {
      formData.value.promotion_reward = {
        ...formData.value.promotion_reward,
        ...patch,
      } as PromotionReward;
    };

    const reward = computed(() => formData.value.promotion_reward as any);

    return () => (
      <RightSheet
        {...{
          isOpen: true,
          onClose: props.onClose,
          title: (
            <div class="flex flex-1 items-center text-2xl text-accent">
              {props.campaign ? "Edit Campaign" : "New Campaign"}
            </div>
          ),
        }}
      >
        <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div class="min-h-0 flex-1 overflow-y-auto p-6 space-y-6">
          {error.value && (
            <div class="alert alert-error text-sm">
              {(error.value as any)?.message ?? "Something went wrong"}
            </div>
          )}

          {/* Basic Info */}
          <section class="space-y-4">
            <h3 class="text-lg font-semibold text-accent">Basic Info</h3>

            <div class="form-control">
              <label class="label">
                <span class="label-text text-accent font-medium">Campaign Name *</span>
              </label>
              <input
                type="text"
                value={formData.value.name}
                onInput={(e) => (formData.value.name = (e.target as HTMLInputElement).value)}
                class="input input-bordered focus:input-primary"
                placeholder="e.g. Lapsed Customer Win-back"
              />
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text text-accent font-medium">Channels</span>
              </label>
              <div class="flex gap-2 flex-wrap">
                {CHANNEL_OPTIONS.map((ch) => (
                  <button
                    key={ch.value}
                    type="button"
                    onClick={() => toggleChannel(ch.value)}
                    class={`btn btn-sm gap-2 ${
                      formData.value.channels.includes(ch.value)
                        ? "btn-primary"
                        : "btn-ghost border border-base-content/20"
                    }`}
                  >
                    <span class="material-symbols-outlined text-base">{ch.icon}</span>
                    {ch.label}
                  </button>
                ))}
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text text-accent font-medium">Coupon Expiry</span>
                <span class="label-text-alt text-base-content/40">days after sending (empty = no expiry)</span>
              </label>
              <input
                type="number"
                value={formData.value.coupon_expires_in_days ?? ""}
                onInput={(e) => {
                  const v = (e.target as HTMLInputElement).value;
                  formData.value.coupon_expires_in_days = v ? parseInt(v) : null;
                }}
                class="input input-bordered focus:input-primary w-32"
                min={1}
                placeholder="30"
              />
            </div>
          </section>

          <div class="divider my-0" />

          {/* Targeting */}
          <section class="space-y-3">
            <div>
              <h3 class="text-lg font-semibold text-accent">Audience Targeting</h3>
              <p class="text-sm text-base-content/50 mt-0.5">
                Who should receive this campaign?
              </p>
            </div>
            <TargetingEditor
              {...{
                value: formData.value.targeting,
                onChange: (v: any) => (formData.value.targeting = v),
              }}
            />
          </section>

          <div class="divider my-0" />

          {/* Message Template */}
          <section class="space-y-4">
            <div>
              <h3 class="text-lg font-semibold text-accent">Message Template</h3>
              <p class="text-sm text-base-content/50 mt-0.5">
                Use{" "}
                {TEMPLATE_VARS.map((v, i) => (
                  <span key={v}>
                    <code class="text-xs bg-base-300 px-1 rounded">{v}</code>
                    {i < TEMPLATE_VARS.length - 1 ? ", " : ""}
                  </span>
                ))}{" "}
                as placeholders.
              </p>
            </div>

            {formData.value.channels.includes("email") && (
              <div class="form-control">
                <label class="label">
                  <span class="label-text text-accent font-medium">Subject</span>
                </label>
                <input
                  type="text"
                  value={formData.value.message_template.subject ?? ""}
                  onInput={(e) =>
                    (formData.value.message_template = {
                      ...formData.value.message_template,
                      subject: (e.target as HTMLInputElement).value,
                    })
                  }
                  class="input input-bordered focus:input-primary"
                  placeholder="A special offer for {{customer_name}}"
                />
              </div>
            )}

            <div class="form-control">
              <label class="label">
                <span class="label-text text-accent font-medium">Body</span>
              </label>
              <textarea
                value={formData.value.message_template.body}
                onInput={(e) =>
                  (formData.value.message_template = {
                    ...formData.value.message_template,
                    body: (e.target as HTMLTextAreaElement).value,
                  })
                }
                class="textarea textarea-bordered focus:textarea-primary h-32"
                placeholder="Hi {{customer_name}}, here is your coupon: {{coupon_code}}"
              />
            </div>
          </section>

          <div class="divider my-0" />

          {/* Reward */}
          {!props.campaign && (
            <section class="space-y-4">
              <div>
                <h3 class="text-lg font-semibold text-accent">Reward</h3>
                <p class="text-sm text-base-content/50 mt-0.5">
                  What discount does the coupon give?
                </p>
              </div>

              <div class="flex gap-2">
                <button
                  type="button"
                  onClick={() => updateReward({ type: "order_discount" })}
                  class={`btn btn-sm flex-1 ${reward.value.type === "order_discount" ? "btn-primary" : "btn-ghost border border-base-content/20"}`}
                >
                  Order Discount
                </button>
                <button
                  type="button"
                  onClick={() => updateReward({ type: "product_discount" })}
                  class={`btn btn-sm flex-1 ${reward.value.type === "product_discount" ? "btn-primary" : "btn-ghost border border-base-content/20"}`}
                >
                  Product Discount
                </button>
              </div>

              <div class="flex gap-3 items-center">
                <select
                  value={reward.value.mode}
                  onChange={(e) => updateReward({ mode: (e.target as HTMLSelectElement).value as any })}
                  class="select select-bordered select-sm"
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="absolute">Fixed Amount ($)</option>
                </select>
                <input
                  type="number"
                  value={reward.value.value}
                  onInput={(e) =>
                    updateReward({ value: parseFloat((e.target as HTMLInputElement).value) || 0 })
                  }
                  class="input input-bordered input-sm w-24"
                  min={0}
                  step={reward.value.mode === "percentage" ? 1 : 0.01}
                />
                <span class="text-base-content/50 text-sm">
                  {reward.value.mode === "percentage" ? "%" : "off"}
                </span>
              </div>
            </section>
          )}
        </div>
        <div class="sticky bottom-0 shrink-0 border-t bg-base-100 p-4">
          <button
            type="button"
            onClick={handleSave}
            class="btn btn-primary btn-block"
            disabled={!isValid.value || saving.value}
          >
            {saving.value && (
              <span class="loading loading-spinner loading-sm mr-2" />
            )}
            {props.campaign ? "Save" : "Create"}
          </button>
        </div>
        </div>
      </RightSheet>
    );
  },
});
